/**
 * AICommandCenter — overview / dashboard for AI Studio.
 *
 * • Hub cards → /ai-hub?promptHub={key} (opens AI Studio with prompt library
 *   pre-filtered to that hub). The old standalone /hubs/{key} pages no longer
 *   fit the AI-Studio-centric model, so we don't link to them.
 * • Recent Conversations → fetches AI Studio threads (the central brain) and
 *   links to /ai-hub?thread={id} so the user can jump straight into context.
 */

import { useState, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { useWorkspace } from '../context/WorkspaceContext';
import { apiFetch } from '../fetchInterceptor';
import { Card, CardContent } from '../components/ui/card';
import { Skeleton } from '../components/ui/skeleton';
import { Badge } from '../components/ui/badge';
import {
  Sparkles, MessageSquare, ArrowRight, Clock, Plus,
} from 'lucide-react';
import * as LucideIcons from 'lucide-react';
import { cn } from '../lib/utils';
import { CBLogo } from '../components/CBLogo';
import { HUB_DEFINITIONS } from '../lib/hubPrompts';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const colorMap = {
  amber: 'from-amber-500 to-orange-500',
  rose: 'from-rose-500 to-pink-500',
  violet: 'from-violet-500 to-fuchsia-500',
  sky: 'from-sky-500 to-cyan-500',
  emerald: 'from-emerald-500 to-teal-500',
  green: 'from-green-500 to-emerald-500',
  blue: 'from-blue-500 to-indigo-500',
  yellow: 'from-yellow-500 to-amber-500',
};

function timeAgo(iso) {
  if (!iso) return '';
  const diff = (new Date() - new Date(iso)) / 1000;
  if (diff < 60) return 'just now';
  if (diff < 3600) return `${Math.floor(diff / 60)}m ago`;
  if (diff < 86400) return `${Math.floor(diff / 3600)}h ago`;
  if (diff < 604800) return `${Math.floor(diff / 86400)}d ago`;
  return new Date(iso).toLocaleDateString();
}

export default function AICommandCenter() {
  const { currentWorkspace } = useWorkspace();
  const wsId = currentWorkspace?.workspace_id;
  const [recent, setRecent] = useState([]);
  const [loading, setLoading] = useState(true);

  // Pull AI Studio threads (the central brain), newest first.
  const fetchRecent = useCallback(async () => {
    setLoading(true);
    try {
      const res = await apiFetch(`${API_URL}/api/ai-hub/threads`);
      if (res.ok) {
        const data = await res.json();
        setRecent((data.threads || []).slice(0, 12));
      }
    } catch (e) { console.error(e); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { fetchRecent(); }, [fetchRecent]);

  const hubs = Object.values(HUB_DEFINITIONS);

  return (
    <div className="space-y-8" data-testid="ai-command-center">
      {/* Hero */}
      <div className="rounded-2xl border bg-gradient-to-br from-emerald-500/10 via-amber-500/5 to-emerald-500/10 p-6">
        <div className="flex items-center gap-3 mb-2">
          <CBLogo size="2xl" className="flex-shrink-0" />
          <div>
            <h1 className="text-3xl font-bold tracking-tight">Prompt Launchpad</h1>
            <p className="text-sm text-muted-foreground">Browse curated AI prompts and jump back into recent chats</p>
          </div>
          <Link
            to="/ai-hub"
            className="ml-auto inline-flex items-center gap-1.5 px-4 py-2 rounded-lg bg-emerald-500 hover:bg-emerald-400 text-black font-medium text-sm transition-colors"
            data-testid="open-ai-studio-btn"
          >
            <Plus className="w-4 h-4" /> New chat
          </Link>
        </div>
        <p className="text-sm text-muted-foreground max-w-2xl mt-2">
          Pick a category below to open AI Studio with a tailored prompt library, or jump back into a recent thread.
        </p>
      </div>

      {/* Hubs Grid → Each card opens AI Studio with prompt library filtered to that hub */}
      <div>
        <h2 className="text-sm font-semibold uppercase tracking-wider text-muted-foreground mb-3">Prompt Categories</h2>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3">
          {hubs.map(hub => {
            const Icon = LucideIcons[hub.icon] || Sparkles;
            const gradient = colorMap[hub.color] || colorMap.blue;
            return (
              <Link
                key={hub.key}
                to={`/ai-hub?promptHub=${hub.key}`}
                className="group rounded-xl border bg-card p-4 hover:shadow-lg hover:-translate-y-0.5 transition-all"
                data-testid={`hub-card-${hub.key}`}
              >
                <div className={cn("w-10 h-10 rounded-lg mb-3 flex items-center justify-center text-white shadow-sm bg-gradient-to-br", gradient)}>
                  <Icon className="w-5 h-5" />
                </div>
                <h3 className="text-sm font-semibold mb-0.5">{hub.name.replace(' Hub', '')}</h3>
                <p className="text-xs text-muted-foreground line-clamp-2">{hub.tagline}</p>
                <div className="flex items-center gap-1 mt-3 text-xs font-medium opacity-0 group-hover:opacity-100 transition-opacity">
                  Open prompts <ArrowRight className="w-3 h-3" />
                </div>
              </Link>
            );
          })}
        </div>
      </div>

      {/* Recent AI Studio Conversations */}
      <div>
        <div className="flex items-center gap-2 mb-3">
          <Clock className="w-4 h-4 text-muted-foreground" />
          <h2 className="text-sm font-semibold uppercase tracking-wider text-muted-foreground">Recent AI Activity</h2>
          {recent.length > 0 && <Badge variant="secondary" className="text-[10px]">{recent.length}</Badge>}
          <Link to="/ai-hub" className="ml-auto text-xs text-muted-foreground hover:text-foreground underline-offset-2 hover:underline" data-testid="open-ai-studio-link">
            Open AI Studio →
          </Link>
        </div>
        {loading ? (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
            {[1, 2, 3, 4].map(i => <Skeleton key={i} className="h-16" />)}
          </div>
        ) : recent.length === 0 ? (
          <Card className="border-dashed">
            <CardContent className="py-12 text-center">
              <MessageSquare className="w-10 h-10 mx-auto mb-2 opacity-30" />
              <p className="text-sm text-muted-foreground">No conversations yet</p>
              <Link to="/ai-hub" className="text-xs text-emerald-600 dark:text-emerald-400 mt-1 inline-flex items-center gap-1 hover:underline">
                Start your first chat <ArrowRight className="w-3 h-3" />
              </Link>
            </CardContent>
          </Card>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
            {recent.map(t => (
              <Link
                key={t.thread_id}
                to={`/ai-hub?thread=${t.thread_id}`}
                className="flex items-center gap-3 rounded-lg border bg-card hover:bg-muted/40 p-3 transition-colors"
                data-testid={`recent-thread-${t.thread_id}`}
              >
                <div className="w-8 h-8 rounded-lg bg-emerald-500/10 flex items-center justify-center flex-shrink-0">
                  <MessageSquare className="w-4 h-4 text-emerald-600 dark:text-emerald-400" />
                </div>
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-medium truncate">{t.title || 'Untitled'}</p>
                  <p className="text-[11px] text-muted-foreground">{t.message_count || 0} messages · {timeAgo(t.updated_at)}</p>
                </div>
                <ArrowRight className="w-4 h-4 text-muted-foreground flex-shrink-0" />
              </Link>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
